import { useState } from "react";
import styled from "styled-components";
import {
  Alert,
  Card,
  Collapse,
  Form,
  Input,
  InputNumber,
  Result,
  Select,
  Space,
  Typography,
} from "antd";
import {
  Authenticated,
  CanAccess,
  useNotification,
  useTranslate,
} from "@refinedev/core";
import { Button, PageTitle } from "@libertify/ui";
import { QuotaFeatures } from "@/constants";
import { OrganizationSubscription } from "@/types";
import useIsAdmin from "@/hooks/useIsAdmin";
import { useManageSubscription } from "./hooks/useManageSubscription";
import {
  buildProductPayload,
  shouldCreateSubscription,
  SubscriptionValidity,
} from "./helpers";

interface FormValues {
  // InputNumber can yield null when cleared, hence the nullable numeric fields.
  videoSeconds: number | null;
  seats: number | null;
  workspaces: number | null;
  publications: number | null;
  concurrentGenerations: number | null;
  validity: SubscriptionValidity;
}

const ManageSubscriptionsPage = () => {
  const t = useTranslate();
  const { open } = useNotification();
  const isAdmin = useIsAdmin();
  const [form] = Form.useForm<FormValues>();
  const { lookupSubscription, submitProduct, isLoading, isSubmitting } =
    useManageSubscription();

  const watchedEntitlements = [
    Form.useWatch("videoSeconds", form),
    Form.useWatch("seats", form),
    Form.useWatch("workspaces", form),
    Form.useWatch("publications", form),
    Form.useWatch("concurrentGenerations", form),
  ];
  const hasChanges = watchedEntitlements.some(
    (value) => typeof value === "number" && value > 0
  );

  const [orgId, setOrgId] = useState("");
  const [lookedUpOrgId, setLookedUpOrgId] = useState<string | null>(null);
  const [subscription, setSubscription] =
    useState<OrganizationSubscription | null>(null);
  const hasLookedUp = lookedUpOrgId !== null;
  const isCreate = shouldCreateSubscription(subscription);

  const handleOrgIdChange = (value: string) => {
    setOrgId(value);
    // Force a fresh lookup whenever the target org changes.
    if (hasLookedUp) {
      setSubscription(null);
      setLookedUpOrgId(null);
    }
  };

  const handleLookup = async () => {
    const trimmed = orgId.trim();
    if (!trimmed) return;

    let sub: OrganizationSubscription | null;
    try {
      sub = await lookupSubscription(trimmed);
    } catch {
      // A non-404 failure: don't assume "no subscription" — surface it instead.
      open?.({
        type: "error",
        message: t("pages.manageSubscriptions.notifications.errorTitle"),
        description: t("pages.manageSubscriptions.notifications.lookupError"),
      });
      return;
    }

    setSubscription(sub);
    setLookedUpOrgId(trimmed);

    const creating = shouldCreateSubscription(sub);
    form.setFieldsValue({
      videoSeconds: null,
      seats: creating ? 1 : null,
      workspaces: creating ? 1 : null,
      publications: creating ? 10 : null,
      concurrentGenerations: creating ? 3 : null,
      validity: "month",
    });
  };

  const handleSubmit = async (formValues: FormValues) => {
    if (!lookedUpOrgId) return;

    const body = buildProductPayload({
      values: {
        videoSeconds: formValues.videoSeconds ?? 0,
        seats: formValues.seats ?? 0,
        workspaces: formValues.workspaces ?? 0,
        publications: formValues.publications ?? 0,
        concurrentGenerations: formValues.concurrentGenerations ?? 0,
      },
      validity: formValues.validity,
      subscription,
    });

    try {
      const updated = await submitProduct(lookedUpOrgId, body);
      open?.({
        type: "success",
        message: t("pages.manageSubscriptions.notifications.successTitle"),
        description: t(
          isCreate
            ? "pages.manageSubscriptions.notifications.createdDescription"
            : "pages.manageSubscriptions.notifications.addedDescription"
        ),
      });
      if (updated) setSubscription(updated);
      form.setFieldsValue({
        videoSeconds: null,
        seats: null,
        workspaces: null,
        publications: null,
        concurrentGenerations: null,
      });
    } catch {
      open?.({
        type: "error",
        message: t("pages.manageSubscriptions.notifications.errorTitle"),
        description: t(
          "pages.manageSubscriptions.notifications.errorDescription"
        ),
      });
    }
  };

  const planName =
    subscription?.subscribed_products?.find((p) => p.product_type === "Plan")
      ?.product_name ||
    subscription?.license_name ||
    t("pages.manageSubscriptions.unknownPlan");

  const formatQuota = (feature: QuotaFeatures, limitOnly = false): string => {
    const entry = subscription?.features?.[feature];
    const limit = entry?.quota_limit ?? 0;
    const usage = entry?.usage ?? 0;
    return limitOnly ? `${limit}` : `${usage} / ${limit}`;
  };

  const summaryRows = subscription
    ? [
        {
          label: t("pages.manageSubscriptions.fields.concurrentGenerations"),
          value: formatQuota(QuotaFeatures.ConcurrentMediaGeneration, true),
        },
        {
          label: t("pages.manageSubscriptions.fields.videoSeconds"),
          value: formatQuota(QuotaFeatures.MediaProject),
        },
        {
          label: t("pages.manageSubscriptions.fields.publications"),
          value: formatQuota(QuotaFeatures.ProjectPublication),
        },
        {
          label: t("pages.manageSubscriptions.fields.seats"),
          value: formatQuota(QuotaFeatures.UserLicenseAssignment),
        },
        {
          label: t("pages.manageSubscriptions.fields.workspaces"),
          value: formatQuota(QuotaFeatures.Workspace),
        },
      ]
    : [];

  return (
    <Authenticated key="manage_subscriptions">
      <CanAccess
        resource="manage_subscriptions"
        action="show"
        fallback={
          <Result
            status="403"
            title={t("pages.manageSubscriptions.accessForbidden")}
          />
        }
      >
        <PageContainer>
          <PageTitle
            title={t("pages.manageSubscriptions.title")}
            description={t("pages.manageSubscriptions.subtitle")}
          />

          <Card style={{ marginTop: 16 }}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Form.Item
                label={t("pages.manageSubscriptions.fields.organizationId")}
                required
                style={{ marginBottom: 0 }}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    value={orgId}
                    onChange={(e) => handleOrgIdChange(e.target.value)}
                    placeholder={t(
                      "pages.manageSubscriptions.fields.organizationIdPlaceholder"
                    )}
                    onPressEnter={() => handleLookup()}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => handleLookup()}
                    loading={isLoading}
                    disabled={!orgId.trim()}
                  >
                    {t("pages.manageSubscriptions.actions.lookup")}
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Space>
          </Card>

          {hasLookedUp && (
            <Card style={{ marginTop: 16 }}>
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                {isCreate ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={t(
                      subscription
                        ? "pages.manageSubscriptions.banners.freeSubscription"
                        : "pages.manageSubscriptions.banners.noSubscription"
                    )}
                  />
                ) : (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      message={t(
                        "pages.manageSubscriptions.banners.existingSubscription",
                        { plan: planName }
                      )}
                      description={t(
                        "pages.manageSubscriptions.banners.addOnTopHint"
                      )}
                    />
                    <div>
                      <Typography.Text strong>
                        {t("pages.manageSubscriptions.currentEntitlements")}
                      </Typography.Text>
                      {summaryRows.map((row) => (
                        <SummaryRow key={row.label}>
                          <Typography.Text type="secondary">
                            {row.label}
                          </Typography.Text>
                          <Typography.Text>{row.value}</Typography.Text>
                        </SummaryRow>
                      ))}
                    </div>
                  </>
                )}

                <Form
                  form={form}
                  layout="horizontal"
                  labelAlign="left"
                  labelCol={{ flex: "180px" }}
                  wrapperCol={{ flex: "auto" }}
                  onFinish={(values) => void handleSubmit(values)}
                  initialValues={{ validity: "month" }}
                >
                  <Form.Item
                    name="videoSeconds"
                    label={t("pages.manageSubscriptions.fields.videoSeconds")}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>

                  {isCreate && (
                    <Form.Item
                      name="validity"
                      label={t("pages.manageSubscriptions.fields.validity")}
                    >
                      <Select
                        options={[
                          {
                            value: "month",
                            label: t(
                              "pages.manageSubscriptions.validityOptions.month"
                            ),
                          },
                          {
                            value: "year",
                            label: t(
                              "pages.manageSubscriptions.validityOptions.year"
                            ),
                          },
                        ]}
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    name="seats"
                    label={t("pages.manageSubscriptions.fields.seats")}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>

                  <Form.Item
                    name="workspaces"
                    label={t("pages.manageSubscriptions.fields.workspaces")}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>

                  <Form.Item
                    name="publications"
                    label={t("pages.manageSubscriptions.fields.publications")}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>

                  {/* Concurrent generations is an admin-only advanced control. */}
                  {isAdmin && (
                    <Collapse
                      ghost
                      items={[
                        {
                          key: "advanced",
                          label: t("pages.manageSubscriptions.advanced"),
                          children: (
                            <Form.Item
                              name="concurrentGenerations"
                              label={t(
                                "pages.manageSubscriptions.fields.concurrentGenerations"
                              )}
                              extra={t(
                                "pages.manageSubscriptions.fields.concurrentGenerationsHint"
                              )}
                            >
                              <InputNumber min={0} style={{ width: "100%" }} />
                            </Form.Item>
                          ),
                        },
                      ]}
                    />
                  )}

                  <Button
                    variant="primary"
                    onClick={() => form.submit()}
                    loading={isSubmitting}
                    disabled={!hasChanges}
                    style={{ marginTop: 16 }}
                  >
                    {t(
                      isCreate
                        ? "pages.manageSubscriptions.actions.createSubscription"
                        : "pages.manageSubscriptions.actions.saveChanges"
                    )}
                  </Button>
                </Form>
              </Space>
            </Card>
          )}
        </PageContainer>
      </CanAccess>
    </Authenticated>
  );
};

export default ManageSubscriptionsPage;
export { ManageSubscriptionsPage };

const PageContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 24px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
`;
